import React from "react";
import { Row, Col, Typography, Divider, Button } from "antd";
import { MailOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const AfricaScholarships = () => {
  return (
    <div className="p-8 max-w-7xl mx-auto" style={{ background: 'linear-gradient(135deg, #e6fffb, #fff7e6)', minHeight: '100vh' }}>
      <Title level={1} className="text-center mb-12" style={{ fontSize: '25px', color: '#13c2c2', fontWeight: '700' }}>
        Africa Scholarships
      </Title>

      {/* Overview Section */}
      <section className="mb-16">
        <Title level={2} style={{ fontSize: '20px', color: '#ffffff', background: 'linear-gradient(45deg, #13c2c2, #08979c)', padding: '8px 16px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
          Overview
        </Title>
        <Paragraph style={{ fontSize: '18px', color: '#595959', lineHeight: '1.6', maxWidth: '900px', margin: '0 auto' }}>
          JectHub is committed to empowering African students with scholarships, mentorship, and career opportunities. Explore a curated list of global companies and universities offering scholarships in AI, Cybersecurity, and Data Science to fuel your future.
        </Paragraph>
      </section>

      {/* Companies Offering Scholarships Section */}
      <section className="mb-16">
        <Title level={2} style={{ fontSize: '20px', color: '#ffffff', background: 'linear-gradient(45deg, #fa8c16, #d46b08)', padding: '8px 16px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
          Big Companies Offering Scholarships
        </Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <ul className="list-none pl-0 text-lg text-gray-700">
              {[
                "Google Africa Developer Scholarships",
                "Microsoft AI & Cloud Scholarships",
                "Meta Data Science & AI Research Scholarships",
                "IBM Skills Academy & Research Grants",
                "AWS AI & Cloud Computing Scholarships",
                "Intel AI for Good Scholarships",
                "Apple Entrepreneur Camp Scholarships",
                "Tesla AI & Engineering Scholarships",
                "Siemens Foundation Scholarships",
                "SAP Africa Code Week Scholarships",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-teal-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-orange-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>
          </Col>
          <Col xs={24} md={12}>
            <ul className="list-none pl-0 text-lg text-gray-700">
              {[
                "Dell STEM and IT Scholarships",
                "Oracle Academy Scholarships",
                "NVIDIA Deep Learning & AI Research Grants",
                "Huawei Seeds for the Future Scholarships",
                "Samsung AI & Tech Development Grants",
                "Cisco Networking Academy Scholarships",
                "Ericsson AI for Africa Program",
                "Uber Future Tech Leaders Scholarships",
                "Vodafone Foundation Scholarships",
                "Schlumberger STEM Scholarships",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-teal-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      </section>

      {/* Universities Offering Scholarships Section */}
      <section className="mb-16">
        <Title level={2} style={{ fontSize: '20px', color: '#ffffff', background: 'linear-gradient(45deg, #13c2c2, #08979c)', padding: '8px 16px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
          Universities Offering Scholarships
        </Title>
        <Row gutter={[24, 24]}>
          {/* North America & Europe */}
          <Col xs={24} md={12}>
            <Title level={3} style={{ fontSize: '22px', color: '#13c2c2', marginBottom: '16px', fontWeight: '600' }}>
              North America
            </Title>
            <ul className="list-none pl-0 text-lg text-gray-700 mb-8">
              {[
                "Harvard Fellowships for African Students",
                "MIT-Africa Initiative Scholarships",
                "Stanford Africa MBA Fellowship",
                "Yale Young African Scholars Program",
                "University of Toronto Mastercard Foundation Scholars Program",
                "Carnegie Mellon University Africa Graduate Scholarships",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-teal-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-orange-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>

            <Title level={3} style={{ fontSize: '20px', color: '#13c2c2', marginBottom: '16px', fontWeight: '600' }}>
              Europe
            </Title>
            <ul className="list-none pl-0 text-lg text-gray-700">
              {[
                "Rhodes Scholarships for Africa",
                "Cambridge Africa Scholarships",
                "AI & Data Science Scholarships at Imperial College London",
                "Mastercard Foundation Scholars Program at University of Edinburgh",
                "Emile Boutmy Scholarships at Sciences Po, France",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-teal-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-orange-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>
          </Col>

          {/* Asia & Middle East, Africa */}
          <Col xs={24} md={12}>
            <Title level={3} style={{ fontSize: '20px', color: '#fa8c16', marginBottom: '16px', fontWeight: '600' }}>
              Asia & Middle East
            </Title>
            <ul className="list-none pl-0 text-lg text-gray-700 mb-8">
              {[
                "Schwarzman Scholars Program at Tsinghua University",
                "Yenching Academy Scholarships at Peking University",
                "AI & Technology Scholarships at NUS",
                "King Abdullah Scholarship Program at KAUST",
                "African Student Scholarships at Tokyo Institute of Technology",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-teal-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>

            <Title level={3} style={{ fontSize: '20px', color: '#fa8c16', marginBottom: '16px', fontWeight: '600' }}>
              Africa
            </Title>
            <ul className="list-none pl-0 text-lg text-gray-700">
              {[
                "Mastercard Foundation Scholars at University of Cape Town",
                "AI & Data Science Scholarships at University of the Witwatersrand",
                "Engineering & Leadership Scholarships at Ashesi University",
                "Full AI & Machine Learning Scholarships at AIMS",
                "AI, Cybersecurity, and Data Science Scholarships at Strathmore University",
              ].map((scholarship, index) => (
                <li key={index} className="mb-4 flex items-start scholarship-item">
                  <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="hover:text-teal-600 transition-colors duration-300">{scholarship}</span>
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      </section>

      {/* How to Apply Section */}
      <section className="mb-16">
        <Title level={2} style={{ fontSize: '20px', color: '#ffffff', background: 'linear-gradient(45deg, #fa8c16, #d46b08)', padding: '8px 16px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
          How to Apply
        </Title>
        <ul className="list-none pl-0 text-lg text-gray-700">
          {[
            "Research each scholarship program and check eligibility requirements.",
            "Prepare necessary documents (CV, transcripts, essays, recommendations).",
            "Submit applications before the deadline on the respective university or company’s website.",
            "Follow up and engage with mentorship programs offered alongside scholarships.",
          ].map((step, index) => (
            <li key={index} className="mb-4 flex items-start scholarship-item">
              <span className="inline-block w-6 h-6 bg-teal-500 text-white rounded-full mr-3 flex-shrink-0 text-center font-bold">{index + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* JectHub’s Commitment Section */}
      <section className="mb-16">
        <Title level={2} style={{ fontSize: '20px', color: '#ffffff', background: 'linear-gradient(45deg, #13c2c2, #08979c)', padding: '8px 16px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
          JectHub’s Commitment
        </Title>
        <Paragraph style={{ fontSize: '18px', color: '#595959', lineHeight: '1.6', maxWidth: '900px', margin: '0 auto 24px' }}>
          JectHub partners with global organizations to provide mentorship, AI training, and resources for African students. We’re here to bridge the gap between education and employment, connecting you with scholarship opportunities and industry leaders.
        </Paragraph>
        <div className="text-center">
          <Button
            type="primary"
            icon={<MailOutlined />}
            href="mailto:scholarships@jecthub.com"
            style={{
              background: 'linear-gradient(45deg, #fa8c16, #d46b08)',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              height: '48px',
              padding: '0 24px',
              fontSize: '12px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #d46b08, #ad4e00)';
              e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #fa8c16, #d46b08)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
            }}
          >
            Contact Us: scholarships@jecthub.com
          </Button>
        </div>
      </section>

      <Divider style={{ borderColor: '#13c2c2', margin: '40px 0' }} />

      {/* Custom CSS */}
      <style jsx>{`
        .scholarship-item {
          animation: fadeIn 0.5s ease-in-out;
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AfricaScholarships;